/**
 * @file Attachment API helpers for parent-board detail views.
 */

import {
    getDownloadURL as storageGetDownloadURL,
    ref as storageRef,
    uploadBytesResumable as storageUploadBytesResumable,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-storage.js";

import {
    createAttachmentBulkDownloadCallable as configuredCreateAttachmentBulkDownloadCallable,
    deleteAttachmentCallable as configuredDeleteAttachmentCallable,
    finalizeAttachmentUploadCallable as configuredFinalizeAttachmentUploadCallable,
    finalizeAttachmentVersionUploadCallable as configuredFinalizeAttachmentVersionUploadCallable,
    getAttachmentOfficePreviewCallable as configuredGetAttachmentOfficePreviewCallable,
    updateAttachmentMetadataCallable as configuredUpdateAttachmentMetadataCallable,
    validateFileUploadCallable as configuredValidateFileUploadCallable,
} from "../../app/firebase/callables.js";
import {
    storage as configuredStorage,
} from "../../app/firebase/firebase.js";
import {
    appDataDetails as configuredAppDataDetails,
    state as configuredState,
} from "../../app/state/state.js";
import { renderDetailsView as configuredRenderDetailsView } from "../../app/detail/detailController.js";
import { buildAttachmentDetailPath } from "../../app/routing/detailRoutes.js";
import { getAttachmentFileCategory } from "../attachments/attachmentModel.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../ui/confirmationModal.js";
import {
    getCollectionName as configuredGetCollectionName,
    getItemType as configuredGetItemType,
} from "../utils/itemLookup.js";

const SUPPORTED_ATTACHMENT_ITEM_TYPES = [
    'patient',
    'project',
    'asset',
    'task',
    'lab',
    'maintenance',
];
const MAX_ATTACHMENT_UPLOAD_FILES = 5;

/**
 * Returns the current attachment upload status elements.
 * @returns {object} Upload status elements.
 */
function getUploadStatusElements() {
    const container = document.getElementById('attachment-upload-status');
    return {
        container,
        detail: document.getElementById('attachment-upload-status-detail'),
        fill: document.getElementById('attachment-upload-status-fill'),
        icon: document.getElementById('attachment-upload-status-icon'),
        percent: document.getElementById('attachment-upload-status-percent'),
        title: document.getElementById('attachment-upload-status-title'),
    };
}

/**
 * Updates the inline attachment upload status row.
 * @param {object} params Status params.
 * @param {string} params.title Main status text.
 * @param {string} params.detail Secondary status text.
 * @param {number} params.progress Upload progress percentage.
 * @param {string} [params.icon='upload_file'] Material icon name.
 * @returns {void}
 */
function updateUploadStatus({
    title,
    detail,
    progress,
    icon = 'upload_file',
}) {
    const elements = getUploadStatusElements();
    const safeProgress = Math.max(0, Math.min(100, Number(progress) || 0));
    if (!elements.container) {return;}

    elements.container.classList.remove('hidden');
    if (elements.title) {elements.title.textContent = title;}
    if (elements.detail) {elements.detail.textContent = detail;}
    if (elements.icon) {elements.icon.textContent = icon;}
    if (elements.percent) {elements.percent.textContent = `${Math.round(safeProgress)}%`;}
    if (elements.fill) {elements.fill.style.width = `${safeProgress}%`;}
}

/**
 * Hides the inline attachment upload status row.
 * @returns {void}
 */
function hideUploadStatus() {
    const elements = getUploadStatusElements();
    if (!elements.container) {return;}
    elements.container.classList.add('hidden');
    if (elements.fill) {elements.fill.style.width = '0%';}
    if (elements.percent) {elements.percent.textContent = '0%';}
}

/**
 * Uploads a single attachment file and reports progress.
 * @param {object} params Upload params.
 * @param {Function} params.getDownloadURL Firebase Storage URL resolver.
 * @param {Function} params.onProgress Progress callback.
 * @param {object} params.uploadTask Firebase upload task.
 * @returns {Promise<string>} Download URL.
 */
function uploadAttachmentFile({
    getDownloadURL,
    onProgress,
    uploadTask,
}) {
    return new Promise((resolve, reject) => {
        uploadTask.on(
            'state_changed',
            (snapshot) => {
                onProgress(snapshot.bytesTransferred, snapshot.totalBytes);
            },
            reject,
            async () => {
                try {
                    resolve(await getDownloadURL(uploadTask.snapshot.ref));
                } catch (error) {
                    reject(error);
                }
            },
        );
    });
}

/**
 * Creates attachment API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Attachment API helpers.
 */
export function createAttachmentsApi({
    appDataDetails,
    createAttachmentBulkDownloadCallable,
    deleteAttachmentCallable,
    finalizeAttachmentUploadCallable,
    finalizeAttachmentVersionUploadCallable,
    getAttachmentOfficePreviewCallable,
    getCollectionName,
    getDownloadURL,
    getItemType,
    openConfirmationModal,
    ref,
    renderDetailsView,
    state,
    storage,
    uploadBytesResumable,
    updateAttachmentMetadataCallable,
    validateFileUploadCallable,
}) {
    const resolveItemType = (itemId) => {
        let itemType = getItemType(itemId);
        if (!itemType && appDataDetails.tasks.find((task) => task.id === itemId)) {
            itemType = 'task';
        }
        return itemType;
    };

    /**
     * Creates and opens a ZIP download for selected current attachments.
     * @param {Array<string>} attachmentIds Selected logical attachment ids.
     * @returns {Promise<string>} Copied or generated link.
     */
    const handleAttachmentBulkDownload = async (attachmentIds) => {
        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemType || !Array.isArray(attachmentIds) || !attachmentIds.length) {
            openConfirmationModal(
                'Download Failed',
                'Select at least one attachment to download.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        try {
            const result = await createAttachmentBulkDownloadCallable({
                itemId,
                itemType,
                attachmentIds,
            });
            const downloadUrl = result.data?.url;
            if (!downloadUrl) {
                throw new Error('Download link was not returned.');
            }
            window.open(downloadUrl, '_blank', 'noopener');
        } catch (error) {
            console.error("Bulk attachment download failed:", error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Could not create attachment download: ${error.message}.`;
            openConfirmationModal('Download Failed', errorMessage, async () => { }, false, 'OK');
        }
    };

    /**
     * Deletes selected logical attachments through backend delete calls.
     * @param {Array<object>} attachments Selected normalized attachments.
     * @returns {Promise<void>}
     */
    const handleAttachmentBulkDelete = async (attachments) => {
        const selectedAttachments = Array.isArray(attachments) ? attachments : [];
        if (!selectedAttachments.length) {
            openConfirmationModal(
                'Delete Failed',
                'Select at least one attachment to delete.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);
        if (!itemType) {
            openConfirmationModal(
                'Error',
                'Could not find item to remove attachments from.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        try {
            for (const attachment of selectedAttachments) {
                const currentVersion = attachment.currentVersion;
                if (!currentVersion?.path) {continue;}
                await deleteAttachmentCallable({
                    itemId,
                    itemType,
                    filePath: currentVersion.path,
                    deleteMode: 'attachment',
                    fileToRemove: currentVersion,
                });
            }
            renderDetailsView();
        } catch (error) {
            console.error("Bulk attachment delete failed:", error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Could not delete attachments: ${error.message}.`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
        }
    };

    /**
     * Copies an app-internal attachment link.
     * @param {object} params Link params.
     * @param {object} params.attachment Normalized attachment.
     * @param {object|null} [params.version] Optional version.
     * @returns {Promise<void>}
     */
    const handleAttachmentLinkCopy = async ({
        attachment,
        version = null,
    }) => {
        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);
        const linkPath = buildAttachmentDetailPath(
            itemType,
            itemId,
            attachment?.id,
            version?.id || null,
        );
        if (!linkPath) {
            openConfirmationModal(
                'Link Failed',
                'Could not create a link for this attachment.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        const link = `${window.location.origin}${linkPath}`;
        try {
            await navigator.clipboard.writeText(link);
        } catch (error) {
            console.error("Attachment link copy failed:", error);
            openConfirmationModal(
                'Copy Failed',
                'Could not copy the attachment link. Please try again.',
                async () => { },
                false,
                'OK',
            );
            return null;
        }
        return link;
    };

    /**
     * Handles file upload and writes attachment metadata to the current item.
     * @param {Event} e File input or drop event.
     * @returns {Promise<void>}
     */
    const handleFileUpload = async (e) => {
        const files = Array.from(e.target?.files || []);
        if (!files.length) {
            console.warn("handleFileUpload: No file selected.");
            return;
        }
        if (files.length > MAX_ATTACHMENT_UPLOAD_FILES) {
            openConfirmationModal(
                'Upload Failed',
                `You can upload up to ${MAX_ATTACHMENT_UPLOAD_FILES} files at a time.`,
                async () => { },
                false,
                'OK',
            );
            e.target.value = '';
            return;
        }

        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemId ||
            !itemType ||
            !SUPPORTED_ATTACHMENT_ITEM_TYPES.includes(itemType)) {
            console.error(
                "handleFileUpload: Cannot upload file - No valid item selected or unsupported item type.",
            );
            openConfirmationModal(
                'Error',
                'Cannot upload file: Please ensure an item is selected.',
                async () => { },
                false,
                'OK',
            );
            e.target.value = '';
            return;
        }

        const uploadCount = files.length;
        updateUploadStatus({
            title: uploadCount > 1 ? 'Checking attachments' : 'Checking attachment',
            detail: uploadCount > 1 ? `Preparing ${uploadCount} files` : files[0].name,
            progress: 0,
        });

        const validatedFiles = [];
        try {
            for (let index = 0; index < files.length; index += 1) {
                const file = files[index];
                updateUploadStatus({
                    title: uploadCount > 1 ? 'Checking attachments' : 'Checking attachment',
                    detail: uploadCount > 1 ?
                        `Checking ${index + 1} of ${uploadCount}: ${file.name}` :
                        file.name,
                    progress: (index / uploadCount) * 8,
                });

                const validationResult = await validateFileUploadCallable({
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                });

                if (!validationResult.data.valid) {
                    openConfirmationModal(
                        'Upload Failed',
                        `File validation failed for ${file.name}. Please check the file and try again.`,
                        async () => { },
                        false,
                        'OK',
                    );
                    hideUploadStatus();
                    e.target.value = '';
                    return;
                }

                validatedFiles.push({
                    file,
                    sanitizedFileName: validationResult.data.sanitizedFileName,
                });
            }
        } catch (error) {
            console.error("File validation error:", error);
            const errorMessage = error.message ||
                'File validation failed. Please check the file size, type, and extension.';
            openConfirmationModal('Upload Failed', errorMessage, async () => { }, false, 'OK');
            hideUploadStatus();
            e.target.value = '';
            return;
        }

        const collectionName = getCollectionName(itemType);
        const batchId = Date.now();
        const totalBytes = validatedFiles.reduce((sum, entry) => {
            return sum + (Number(entry.file.size) || 0);
        }, 0) || 1;
        let completedBytes = 0;

        try {
            for (let index = 0; index < validatedFiles.length; index += 1) {
                const {file, sanitizedFileName} = validatedFiles[index];
                const filePath = `${collectionName}/${itemId}/attachments/${batchId}-${index}-${sanitizedFileName}`;
                const fileRef = ref(storage, filePath);
                const uploadTask = uploadBytesResumable(fileRef, file);
                const title = uploadCount > 1 ? 'Uploading attachments' : 'Uploading attachment';

                const downloadURL = await uploadAttachmentFile({
                    getDownloadURL,
                    uploadTask,
                    onProgress: (bytesTransferred) => {
                        const progress = ((completedBytes + bytesTransferred) / totalBytes) * 100;
                        updateUploadStatus({
                            title,
                            detail: uploadCount > 1 ?
                                `Uploading ${index + 1} of ${uploadCount}: ${file.name}` :
                                file.name,
                            progress,
                        });
                    },
                });

                completedBytes += Number(file.size) || 0;

                const fileData = {
                    name: sanitizedFileName,
                    url: downloadURL,
                    path: filePath,
                    size: file.size,
                    type: file.type,
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: state.currentUser?.uid || 'unknown',
                };

                updateUploadStatus({
                    title,
                    detail: uploadCount > 1 ?
                        `Saving ${index + 1} of ${uploadCount}: ${file.name}` :
                        `Saving ${file.name}`,
                    progress: (completedBytes / totalBytes) * 100,
                });
                await finalizeAttachmentUploadCallable({
                    itemId,
                    itemType,
                    file: fileData,
                });
            }

            updateUploadStatus({
                title: uploadCount > 1 ? 'Attachments uploaded' : 'Attachment uploaded',
                detail: uploadCount > 1 ? `${uploadCount} files uploaded` : validatedFiles[0].file.name,
                progress: 100,
                icon: 'check_circle',
            });
            e.target.value = '';
            renderDetailsView();
        } catch (error) {
            console.error("Upload failed:", error.code, error.message);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `File upload failed: ${error.message}. Please try again.`;
            updateUploadStatus({
                title: 'Upload failed',
                detail: error.message || 'Please try again.',
                progress: 0,
                icon: 'error',
            });
            openConfirmationModal('Upload Failed', errorMessage, async () => { }, false, 'OK');
            e.target.value = '';
        }
    };

    /**
     * Uploads a new version for an existing logical attachment.
     * @param {object} params Upload params.
     * @param {object} params.attachment Normalized attachment.
     * @param {File} params.file Selected file.
     * @returns {Promise<void>}
     */
    const handleAttachmentVersionUpload = async ({
        attachment,
        file,
    }) => {
        if (!attachment?.id || !file) {
            console.warn("handleAttachmentVersionUpload: Missing attachment or file.");
            return;
        }
        if (getAttachmentFileCategory(attachment.currentVersion) !==
            getAttachmentFileCategory(file)) {
            openConfirmationModal(
                'Upload Failed',
                'New versions must use the same file category as the current attachment.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemId ||
            !itemType ||
            !SUPPORTED_ATTACHMENT_ITEM_TYPES.includes(itemType)) {
            openConfirmationModal(
                'Error',
                'Cannot upload file: Please ensure an item is selected.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        updateUploadStatus({
            title: 'Checking new version',
            detail: file.name,
            progress: 0,
        });

        let sanitizedFileName;
        try {
            const validationResult = await validateFileUploadCallable({
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
            });

            if (!validationResult.data.valid) {
                openConfirmationModal(
                    'Upload Failed',
                    'File validation failed. Please check the file and try again.',
                    async () => { },
                    false,
                    'OK',
                );
                hideUploadStatus();
                return;
            }

            sanitizedFileName = validationResult.data.sanitizedFileName;
        } catch (error) {
            console.error("File validation error:", error);
            const errorMessage = error.message ||
                'File validation failed. Please check the file size, type, and extension.';
            openConfirmationModal('Upload Failed', errorMessage, async () => { }, false, 'OK');
            hideUploadStatus();
            return;
        }

        const collectionName = getCollectionName(itemType);
        const filePath = `${collectionName}/${itemId}/attachments/${Date.now()}-version-${sanitizedFileName}`;
        const fileRef = ref(storage, filePath);
        const uploadTask = uploadBytesResumable(fileRef, file);

        try {
            const downloadURL = await uploadAttachmentFile({
                getDownloadURL,
                uploadTask,
                onProgress: (bytesTransferred, totalBytes) => {
                    const progress = (bytesTransferred / (totalBytes || 1)) * 100;
                    updateUploadStatus({
                        title: 'Uploading new version',
                        detail: file.name,
                        progress,
                    });
                },
            });

            const fileData = {
                name: sanitizedFileName,
                url: downloadURL,
                path: filePath,
                size: file.size,
                type: file.type,
                uploadedAt: new Date().toISOString(),
                uploadedBy: state.currentUser?.uid || 'unknown',
            };

            updateUploadStatus({
                title: 'Saving new version',
                detail: file.name,
                progress: 100,
            });

            await finalizeAttachmentVersionUploadCallable({
                itemId,
                itemType,
                attachmentId: attachment.id,
                file: fileData,
            });

            updateUploadStatus({
                title: 'Version uploaded',
                detail: file.name,
                progress: 100,
                icon: 'check_circle',
            });
            renderDetailsView();
        } catch (error) {
            console.error("Version upload failed:", error.code, error.message);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Version upload failed: ${error.message}. Please try again.`;
            updateUploadStatus({
                title: 'Upload failed',
                detail: error.message || 'Please try again.',
                progress: 0,
                icon: 'error',
            });
            openConfirmationModal('Upload Failed', errorMessage, async () => { }, false, 'OK');
        }
    };

    /**
     * Deletes an attachment through the backend delete callable.
     * @param {string} filePath Storage path.
     * @param {object} fileObject Stored attachment metadata object.
     * @param {object} [options] Delete options.
     * @param {string} [options.deleteMode='attachment'] Whether to delete one version or all versions.
     * @returns {Promise<void>}
     */
    const handleFileDelete = async (
        filePath,
        fileObject,
        {deleteMode = 'attachment'} = {},
    ) => {
        if (!filePath || !fileObject) {
            console.error("handleFileDelete: Missing filePath or fileObject.");
            return;
        }

        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemType) {
            openConfirmationModal(
                'Error',
                'Could not find item to remove attachment from.',
                async () => { },
                false,
                'OK',
            );
            return;
        }

        try {
            await deleteAttachmentCallable({
                itemId,
                itemType,
                filePath,
                deleteMode,
                fileToRemove: fileObject,
            });

            renderDetailsView();
        } catch (error) {
            console.error(`Error calling deleteAttachmentCallable for ${filePath}:`, error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Could not delete attachment: ${error.message}.`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
        }
    };

    /**
     * Saves logical attachment display metadata.
     * @param {object} params Metadata save parameters.
     * @param {string} params.attachmentId Attachment id.
     * @param {string} params.filePath Current version storage path fallback.
     * @param {string} params.displayName Display name.
     * @param {string} params.description Description.
     * @returns {Promise<void>}
     */
    const handleAttachmentMetadataSave = async ({
        attachmentId,
        filePath,
        displayName,
        description,
    }) => {
        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemType) {
            throw new Error('Could not find item to update attachment metadata.');
        }

        await updateAttachmentMetadataCallable({
            itemId,
            itemType,
            attachmentId,
            filePath,
            displayName,
            description,
        });

        renderDetailsView();
    };

    /**
     * Creates a short-lived Office preview URL for an attachment.
     * @param {object} params Preview parameters.
     * @param {string} params.attachmentId Attachment id.
     * @param {string} params.versionId Version id.
     * @param {string} params.filePath Storage path fallback.
     * @returns {Promise<object>} Preview payload.
     */
    const handleAttachmentOfficePreview = async ({
        attachmentId,
        versionId,
        filePath,
    }) => {
        const itemId = state.selectedItemId;
        const itemType = resolveItemType(itemId);

        if (!itemType) {
            throw new Error('Could not find item to preview attachment.');
        }

        const result = await getAttachmentOfficePreviewCallable({
            itemId,
            itemType,
            attachmentId,
            versionId,
            filePath,
        });

        return result.data;
    };

    return {
        handleAttachmentBulkDelete,
        handleAttachmentBulkDownload,
        handleAttachmentLinkCopy,
        handleAttachmentMetadataSave,
        handleAttachmentOfficePreview,
        handleAttachmentVersionUpload,
        handleFileDelete,
        handleFileUpload,
    };
}

const configuredAttachmentsApi = createAttachmentsApi({
    appDataDetails: configuredAppDataDetails,
    createAttachmentBulkDownloadCallable: configuredCreateAttachmentBulkDownloadCallable,
    deleteAttachmentCallable: configuredDeleteAttachmentCallable,
    finalizeAttachmentUploadCallable: configuredFinalizeAttachmentUploadCallable,
    finalizeAttachmentVersionUploadCallable: configuredFinalizeAttachmentVersionUploadCallable,
    getAttachmentOfficePreviewCallable: configuredGetAttachmentOfficePreviewCallable,
    getCollectionName: configuredGetCollectionName,
    getDownloadURL: storageGetDownloadURL,
    getItemType: configuredGetItemType,
    openConfirmationModal: configuredOpenConfirmationModal,
    ref: storageRef,
    renderDetailsView: configuredRenderDetailsView,
    state: configuredState,
    storage: configuredStorage,
    uploadBytesResumable: storageUploadBytesResumable,
    updateAttachmentMetadataCallable: configuredUpdateAttachmentMetadataCallable,
    validateFileUploadCallable: configuredValidateFileUploadCallable,
});

export const handleAttachmentMetadataSave = configuredAttachmentsApi.handleAttachmentMetadataSave;
export const handleAttachmentBulkDelete = configuredAttachmentsApi.handleAttachmentBulkDelete;
export const handleAttachmentBulkDownload = configuredAttachmentsApi.handleAttachmentBulkDownload;
export const handleAttachmentLinkCopy = configuredAttachmentsApi.handleAttachmentLinkCopy;
export const handleAttachmentOfficePreview = configuredAttachmentsApi.handleAttachmentOfficePreview;
export const handleAttachmentVersionUpload = configuredAttachmentsApi.handleAttachmentVersionUpload;
export const handleFileDelete = configuredAttachmentsApi.handleFileDelete;
export const handleFileUpload = configuredAttachmentsApi.handleFileUpload;
